import api from '../../api/client';
import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Tabs,
  Upload,
  Table,
  Modal,
  message,
  Space,
  DatePicker,
  Switch,
  Spin,
  Alert,
  Row,
  Col,
  Tag,
  Typography,
  Tooltip,
  Badge,
} from "antd";
import {
  SendOutlined,
  UploadOutlined,
  FileExcelOutlined,
  MailOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import * as XLSX from "xlsx";
import axios from "axios";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import config from "../../config";
import { usePermissions } from "../../permissions";

const { TabPane } = Tabs;
const { Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const MessagingCenter = () => {
  const account = useSelector((state) => state.account);
  const [activeTab, setActiveTab] = useState("compose");
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [messageType, setMessageType] = useState("email");
  const [emailContent, setEmailContent] = useState("");
  const [smsContent, setSmsContent] = useState("");
  const [whatsappContent, setWhatsappContent] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [customRecipients, setCustomRecipients] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [excelColumns, setExcelColumns] = useState([]);
  const [excelPreviewVisible, setExcelPreviewVisible] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Category selection states
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryRecipientsList, setCategoryRecipientsList] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);

  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;
  const { createPermission } = usePermissions();

  // Fetch reports
  const fetchReports = async () => {
    setReportsLoading(true);
    try {
      const { data } = await api.get(`/message-batches`);
      setReports(data);
    } catch (error) {
      message.error("Failed to load reports");
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // When category changes, fetch its recipients
  const handleCategoryChange = async (categoryValue) => {
    setSelectedCategory(categoryValue);
    setSelectedRecipients([]);
    if (!categoryValue) {
      setCategoryRecipientsList([]);
      return;
    }
    const categoryMap = {
      Employees: `/users`,
      "Admin Users": `/admins`,
      Customers: `/customer`,
      Suppliers: `/suppliers`,
      Veterinary: `/vetenary`,
    };
    const endpoint = categoryMap[categoryValue];
    if (!endpoint) return;
    try {
      const res = await api.get(endpoint);
      const contactKey = messageType === "email" ? "email" : "phone_number";
      let items = res.data
        .filter((item) => item[contactKey])
        .map((item) => ({
          label: `${item.firstname || item.name || item.label} (${
            item[contactKey]
          })`,
          value: item[contactKey],
        }));
      setCategoryRecipientsList(items);
    } catch (err) {
      message.error("Failed to fetch recipients");
    }
  };

  // Excel upload
  const handleExcelUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet);
      if (json.length > 0) {
        setExcelData(json);
        setExcelColumns(Object.keys(json[0]));
        setExcelPreviewVisible(true);
      } else {
        message.warning("Excel file is empty");
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const renderExcelPreview = () => {
    const columns = excelColumns.map((col) => ({
      title: col,
      dataIndex: col,
      key: col,
      ellipsis: true,
    }));
    return (
      <Modal
        title="Excel Data Preview"
        open={excelPreviewVisible}
        onCancel={() => setExcelPreviewVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setExcelPreviewVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="import"
            type="primary"
            onClick={() => {
              const contactField = messageType === "email" ? "email" : "phone";
              const recipients = excelData
                .map(
                  (row) =>
                    row[contactField] ||
                    row.Email ||
                    row.email ||
                    row.Phone ||
                    row.phone ||
                    row.mobile,
                )
                .filter(Boolean);
              if (recipients.length === 0) {
                message.error(
                  `No ${contactField} column found. Ensure your Excel has an 'email' or 'phone' column.`,
                );
                return;
              }
              setCustomRecipients(recipients.map((contact) => ({ contact })));
              message.success(
                `${recipients.length} recipients loaded from Excel`,
              );
              setExcelPreviewVisible(false);
            }}
          >
            Import Recipients
          </Button>,
        ]}
      >
        <Paragraph>
          First 5 rows of your Excel file. For email, ensure an{" "}
          <strong>"email"</strong> column exists. For SMS/WhatsApp, ensure a{" "}
          <strong>"phone"</strong> column exists.
        </Paragraph>
        <Table
          dataSource={excelData.slice(0, 5)}
          columns={columns}
          pagination={false}
          size="small"
          scroll={{ x: true }}
        />
      </Modal>
    );
  };

  // Submit handler
  const handleSubmit = async (values) => {
    if (!createPermission) {
      message.warning("You don't have permission to send messages");
      return;
    }
    setLoading(true);
    try {
      let recipients = [];
      const recipientType = values.recipientType;

      if (recipientType === "category") {
        recipients = selectedRecipients;
      } else if (recipientType === "custom") {
        recipients = customRecipients.map((r) => r.contact);
      } else if (recipientType === "manual") {
        recipients =
          values.manualRecipients?.split(",").map((r) => r.trim()) || [];
      }

      if (recipients.length === 0) {
        message.warning("No recipients selected");
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("messageType", messageType);
      formData.append("recipients", JSON.stringify(recipients));
      formData.append("subject", values.subject || "");
      formData.append(
        "message",
        messageType === "email"
          ? emailContent
          : messageType === "sms"
          ? smsContent
          : whatsappContent,
      );
      formData.append("scheduled", values.scheduled || false);
      if (values.scheduled && values.scheduleDate) {
        formData.append(
          "sdate",
          values.scheduleDate.format("YYYY-MM-DD HH:mm:ss"),
        );
      }
      if (attachmentFile) {
        formData.append("attachment", attachmentFile);
      }
      formData.append("userId", account.user.id);
      formData.append("userName", account.user.firstname);

      const response = await api.post(`/send-messages`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.success) {
        message.success(response.data.message);
        form.resetFields();
        setEmailContent("");
        setSmsContent("");
        setWhatsappContent("");
        setAttachmentFile(null);
        setCustomRecipients([]);
        setSelectedCategory(null);
        setSelectedRecipients([]);
        setCategoryRecipientsList([]);
        fetchReports();
        setActiveTab("reports");
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  // Reports table columns
  const reportColumns = [
    { title: "ID", dataIndex: "id", key: "id", width: 60 },
    { title: "Subject", dataIndex: "subject", key: "subject", ellipsis: true },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (text) => text || "Custom",
    },
    {
      title: "Type",
      dataIndex: "message_type",
      key: "type",
      render: (t) => {
        let color = "blue";
        let icon = <MailOutlined />;
        if (t === "sms") {
          color = "green";
          icon = <PhoneOutlined />;
        }
        if (t === "whatsapp") {
          color = "green";
          icon = <WhatsAppOutlined />;
        }
        return (
          <Tag color={color} icon={icon}>
            {t?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Scheduled",
      dataIndex: "scheduled",
      key: "scheduled",
      render: (val) => (val ? "Yes" : "No"),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          {record.attachments && (
            <Tooltip title="View Attachment">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() =>
                  window.open(
                    `/uploads/${record.attachments}`,
                    "_blank",
                  )
                }
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record) => {
    const logs = record.logs || [];
    const logColumns = [
      { title: "Recipient", dataIndex: "recipient", key: "recipient" },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (s) => (
          <Badge status={s === "success" ? "success" : "error"} text={s} />
        ),
      },
      { title: "Error", dataIndex: "error", key: "error", ellipsis: true },
      {
        title: "Sent At",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
      },
    ];
    return (
      <Table
        columns={logColumns}
        dataSource={logs}
        rowKey="id"
        pagination={false}
        size="small"
      />
    );
  };

  const charLimit = messageType === "sms" ? 160 : 1000;

  return (
    <Card title="Messaging Center">
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Compose" key="compose">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ recipientType: "category", scheduled: false }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="messageType"
                  label="Message Type"
                  initialValue="email"
                >
                  <Select onChange={setMessageType}>
                    <Option value="email">Email</Option>
                    <Option value="sms">SMS</Option>
                    <Option value="whatsapp">WhatsApp</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="subject"
                  label="Subject"
                  rules={[{ required: messageType === "email" }]}
                >
                  <Input
                    placeholder="Enter subject"
                    disabled={messageType !== "email"}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="recipientType"
              label="Recipient Source"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="category">Choose from category</Option>
                <Option value="custom">Upload Excel list</Option>
                <Option value="manual">Enter manually (comma separated)</Option>
              </Select>
            </Form.Item>

            {/* Category selection */}
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.recipientType !== curr.recipientType
              }
            >
              {({ getFieldValue }) => {
                if (getFieldValue("recipientType") !== "category") return null;
                return (
                  <>
                    <Form.Item label="Select Category">
                      <Select
                        placeholder="Choose a category"
                        options={[
                          { label: "Employees", value: "Employees" },
                          { label: "Admin Users", value: "Admin Users" },
                          { label: "Customers", value: "Customers" },
                          { label: "Suppliers", value: "Suppliers" },
                          { label: "Veterinary", value: "Veterinary" },
                        ]}
                        onChange={handleCategoryChange}
                        value={selectedCategory}
                      />
                    </Form.Item>
                    {categoryRecipientsList.length > 0 && (
                      <Form.Item label="Select Recipients" required>
                        <div>
                          <Button
                            size="small"
                            onClick={() =>
                              setSelectedRecipients(
                                categoryRecipientsList.map((r) => r.value),
                              )
                            }
                          >
                            Select All
                          </Button>
                          <Button
                            size="small"
                            onClick={() => setSelectedRecipients([])}
                            style={{ marginLeft: 8 }}
                          >
                            Clear All
                          </Button>
                        </div>
                        <Select
                          mode="multiple"
                          value={selectedRecipients}
                          onChange={setSelectedRecipients}
                          options={categoryRecipientsList}
                          placeholder="Select recipients"
                          showSearch
                          style={{ width: "100%", marginTop: 8 }}
                        />
                      </Form.Item>
                    )}
                  </>
                );
              }}
            </Form.Item>

            {/* Custom Excel upload */}
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.recipientType !== curr.recipientType
              }
            >
              {({ getFieldValue }) => {
                if (getFieldValue("recipientType") !== "custom") return null;
                return (
                  <div>
                    <Upload
                      beforeUpload={handleExcelUpload}
                      accept=".xlsx, .xls"
                      showUploadList={false}
                    >
                      <Button icon={<FileExcelOutlined />}>
                        Upload Excel File
                      </Button>
                    </Upload>
                    {customRecipients.length > 0 && (
                      <Alert
                        message={`${customRecipients.length} recipients loaded`}
                        type="info"
                        style={{ marginTop: 8 }}
                        action={
                          <Button
                            size="small"
                            onClick={() => setCustomRecipients([])}
                          >
                            Clear
                          </Button>
                        }
                      />
                    )}
                  </div>
                );
              }}
            </Form.Item>

            {/* Manual entry */}
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.recipientType !== curr.recipientType
              }
            >
              {({ getFieldValue }) => {
                if (getFieldValue("recipientType") !== "manual") return null;
                return (
                  <Form.Item
                    name="manualRecipients"
                    label={`Recipients (comma separated) - ${
                      messageType === "email" ? "emails" : "phone numbers"
                    }`}
                    rules={[{ required: true }]}
                  >
                    <TextArea
                      rows={3}
                      placeholder={
                        messageType === "email"
                          ? "email1@example.com, email2@example.com"
                          : "0712345678, 0723456789"
                      }
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            {/* Message content */}
            {messageType === "email" ? (
              <Form.Item label="Message" required>
                <ReactQuill
                  theme="snow"
                  value={emailContent}
                  onChange={setEmailContent}
                  style={{ height: 300 }}
                />
              </Form.Item>
            ) : (
              <Form.Item
                label={`Message (max ${charLimit} characters)`}
                required
              >
                <TextArea
                  rows={4}
                  maxLength={charLimit}
                  showCount
                  value={messageType === "sms" ? smsContent : whatsappContent}
                  onChange={(e) => {
                    if (messageType === "sms") setSmsContent(e.target.value);
                    else setWhatsappContent(e.target.value);
                  }}
                  placeholder={`Type your ${messageType.toUpperCase()} message here...`}
                />
              </Form.Item>
            )}

            <Form.Item label="Attachment (optional)">
              <Upload
                beforeUpload={(file) => {
                  setAttachmentFile(file);
                  return false;
                }}
                maxCount={1}
                fileList={
                  attachmentFile
                    ? [{ uid: "1", name: attachmentFile.name }]
                    : []
                }
                onRemove={() => setAttachmentFile(null)}
              >
                <Button icon={<UploadOutlined />}>Upload File</Button>
              </Upload>
            </Form.Item>

            <Form.Item
              name="scheduled"
              label="Schedule for later"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.scheduled !== curr.scheduled}
            >
              {({ getFieldValue }) =>
                getFieldValue("scheduled") && (
                  <Form.Item
                    name="scheduleDate"
                    label="Schedule Date"
                    rules={[{ required: true }]}
                  >
                    <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" />
                  </Form.Item>
                )
              }
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SendOutlined />}
                  loading={loading}
                >
                  Send Message
                </Button>
                <Button onClick={() => form.resetFields()}>Reset</Button>
              </Space>
            </Form.Item>
          </Form>
        </TabPane>

        <TabPane tab="Reports" key="reports">
          <Spin spinning={reportsLoading}>
            <Table
              size="small"
              columns={reportColumns}
              dataSource={reports}
              rowKey="id"
              expandable={{
                expandedRowRender,
                expandedRowKeys,
                onExpandedRowsChange: setExpandedRowKeys,
              }}
              pagination={{ pageSize: 10 }}
              scroll={{ x: true }}
            />
          </Spin>
        </TabPane>
      </Tabs>

      {renderExcelPreview()}
    </Card>
  );
};

export default MessagingCenter;
